import type {
  DocumentEventCategoryType,
  DocumentViews,
  SearchDocumentViewsRequest,
} from "../../../../api/types/documentActivityTypes";
import type { FilterState } from "../../../common/filters/filterState";
import type {
  DateFilter,
  MultiSelectFilter,
  NumberFilter,
  TableFilter,
  TableFilterDefinition,
  TextFilter,
} from "../../../common/filters/filterTypes";

export const filterDefinitions: TableFilterDefinition<DocumentViews>[] = [
  {
    id: "userName",
    name: "Contact Name",
    type: "text",
    operatorOptions: ["contains"],
  },
  {
    id: "userEmail",
    name: "Contact Email",
    type: "text",
    operatorOptions: ["contains"],
  },
  {
    id: "fileName",
    name: "Document Name",
    type: "text",
    operatorOptions: ["contains"],
  },
  {
    id: "investorIds",
    name: "Investor",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "fundIds",
    name: "Fund",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "categoryType",
    name: "Category Type",
    type: "multi_select",
    operatorOptions: ["one_of"],
    predefinedOptions: [
      { value: "InvestorPortal", label: "Investor Portal" },
      { value: "Fundraising", label: "Fundraising" },
    ],
  },
  {
    id: "categoryIds",
    name: "Category",
    type: "multi_select",
    operatorOptions: ["one_of"],
    asyncOptions: true,
  },
  {
    id: "originatorNo",
    name: "Collection ID",
    type: "number",
    operatorOptions: ["equals"],
  },
  {
    id: "publishedAt",
    name: "Publish Date",
    type: "date",
    operatorOptions: ["range"],
  },
  {
    id: "viewedAt",
    name: "Viewed at",
    type: "date",
    operatorOptions: ["range"],
  },
  {
    id: "completionPercentage",
    name: "Total Completion %",
    type: "number",
    operatorOptions: ["between", "greater_than", "less_than"],
  },
];

export const contactScopedFilterDefinitions: TableFilterDefinition<DocumentViews>[] = filterDefinitions.filter(
  (definition) => definition.id !== "userName" && definition.id !== "userEmail"
);

const getUserNameFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "userName") as TextFilter<DocumentViews> | undefined;
  const value = filter?.value?.operator === "contains" ? filter.value.text : undefined;
  return value || undefined;
};

const getUserEmailFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "userEmail") as TextFilter<DocumentViews> | undefined;
  const value = filter?.value?.operator === "contains" ? filter.value.text : undefined;
  return value || undefined;
};

const getFileNameFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "fileName") as TextFilter<DocumentViews> | undefined;
  const value = filter?.value?.operator === "contains" ? filter.value.text : undefined;
  return value || undefined;
};

const getCategoryTypeFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "categoryType") as MultiSelectFilter<DocumentViews> | undefined;
  const selectedValues =
    filter?.value?.operator === "one_of" ? (filter.value.selectedValues as DocumentEventCategoryType[]) : undefined;
  const value = selectedValues?.length ? selectedValues : undefined;
  return value || undefined;
};

const getCategoryIdsFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "categoryIds") as MultiSelectFilter<DocumentViews> | undefined;
  const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
  return selectedValues?.length ? selectedValues : undefined;
};

const getFundIdsFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "fundIds") as MultiSelectFilter<DocumentViews> | undefined;
  const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
  return selectedValues?.length ? selectedValues : undefined;
};

const getInvestorIdsFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "investorIds") as MultiSelectFilter<DocumentViews> | undefined;
  const selectedValues = filter?.value?.operator === "one_of" ? filter.value.selectedValues : undefined;
  return selectedValues?.length ? selectedValues : undefined;
};

const getOriginatorNoFilterValue = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "originatorNo") as NumberFilter<DocumentViews> | undefined;
  const value = filter?.value?.operator === "equals" ? filter.value.number : undefined;
  return typeof value === "number" ? Math.round(value) : undefined;
};

const getDateRangeFilterValues = (visibleFilters: TableFilter<DocumentViews>[], filterId: string) => {
  const filter = visibleFilters.find((f) => f.id === filterId) as DateFilter<DocumentViews> | undefined;
  if (filter?.value?.operator !== "range") {
    return [undefined, undefined];
  }

  const { range } = filter.value;
  return [range?.[0] || undefined, range?.[1] || undefined];
};

const getCompletionPercentageFilterValues = (visibleFilters: TableFilter<DocumentViews>[]) => {
  const filter = visibleFilters.find((f) => f.id === "completionPercentage") as NumberFilter<DocumentViews> | undefined;

  let completionPercentageFrom: number | undefined = undefined;
  let completionPercentageTo: number | undefined = undefined;

  if (filter?.value?.operator === "between") {
    completionPercentageFrom = filter.value.number;
    completionPercentageTo = filter.value.secondNumber;
  } else if (filter?.value?.operator === "greater_than") {
    completionPercentageFrom = filter.value.number;
  } else if (filter?.value?.operator === "less_than") {
    completionPercentageTo = filter.value.number;
  }

  completionPercentageFrom = toValidIntegerPercentage(completionPercentageFrom);
  completionPercentageTo = toValidIntegerPercentage(completionPercentageTo);

  if (
    completionPercentageFrom !== undefined &&
    completionPercentageTo !== undefined &&
    completionPercentageFrom > completionPercentageTo
  ) {
    completionPercentageFrom = completionPercentageTo;
  }

  return { completionPercentageFrom, completionPercentageTo };
};

const toValidIntegerPercentage = (number: number | undefined) =>
  number !== undefined && !Number.isNaN(number) ? Math.min(Math.max(Math.round(number), 0), 100) : undefined;

export const getSearchDocumentViewsFiltersPayload = (
  filterState: FilterState<DocumentViews>,
  fundraisingId?: string
): Omit<SearchDocumentViewsRequest, "range"> => {
  const [viewedFrom, viewedTo] = getDateRangeFilterValues(filterState.visibleFilters, "viewedAt");

  const [publishedFrom, publishedTo] = getDateRangeFilterValues(filterState.visibleFilters, "publishedAt");

  const { completionPercentageFrom, completionPercentageTo } = getCompletionPercentageFilterValues(
    filterState.visibleFilters
  );

  return {
    userName: getUserNameFilterValue(filterState.visibleFilters),
    userEmail: getUserEmailFilterValue(filterState.visibleFilters),
    fileName: getFileNameFilterValue(filterState.visibleFilters),
    documentEventCategoryTypes: getCategoryTypeFilterValue(filterState.visibleFilters),
    categoryIds: getCategoryIdsFilterValue(filterState.visibleFilters),
    fundIds: getFundIdsFilterValue(filterState.visibleFilters),
    investorIds: getInvestorIdsFilterValue(filterState.visibleFilters),
    originatorNo: getOriginatorNoFilterValue(filterState.visibleFilters),
    publishedFrom,
    publishedTo,
    viewedFrom,
    viewedTo,
    completionPercentageFrom,
    completionPercentageTo,
    fundraisingId,
  };
};
